import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useRef, useState } from "react";
import { useDataContext } from "../hub/data-context-provider.js";
import { CANONICAL_AGENT_V2_SYSTEM_PROMPT_TEMPLATE, V2_DEFAULT_SYSTEM_PROMPT_SETTING_KEY, } from "./agent-v2/defaultSystemPrompt.js";
import { IconCropper, ICON_ALLOWED_ACCEPT } from "./IconCropper.js";
import { computeInitials } from "./WorkspaceList.js";
import { SpendDashboard } from "./agent-v2/SpendDashboard.js";
import { OpenRouterModelPicker } from "./agent-v2/OpenRouterModelPicker.js";
// KTK-275 — workspace-default OpenRouter model setting key. Per-agent
// picks (Agent V2's own `model` field) fall back to this when unset.
// Backend consumption: agent-launch path reads it as the default for
// agents that haven't set their own. Persisted via the generic
// setWorkspaceSetting bag.
const OPENROUTER_DEFAULT_MODEL_KEY = "openrouterDefaultModel";
const DEFAULT_EMOJIS = ["👍", "❤️", "🎉", "👀", "🔥", "✅"];
// Renders a friendly relative-time string for the Profile tab's auto-save
// hint. Resolution is intentionally coarse (5s buckets at the very low end,
// minutes/hours past that) — we update the hint on a 30s tick, so anything
// finer would lie about precision the parent isn't actually polling for.
function formatRelativeTime(d) {
    const sec = Math.floor((Date.now() - d.getTime()) / 1000);
    if (sec < 5)
        return "just now";
    if (sec < 60)
        return `${sec}s ago`;
    if (sec < 3600)
        return `${Math.floor(sec / 60)} min ago`;
    if (sec < 86400)
        return `${Math.floor(sec / 3600)}h ago`;
    return d.toLocaleDateString();
}
/**
 * Workspace settings modal. Matches kritaka-electron's settings surface:
 * user identity (handle, display name, reaction emojis) and the workspace
 * MCP-server config that's merged into every agent's launcher. Values
 * persist via `ctx.hub.setWorkspaceSetting` and `ctx.hub.setMcpJsonConfig`.
 */
export function WorkspaceSettingsDialog({ workspaceId, onClose, onHandleChange, onIconChange, initialSection, inline = false, showSections, }) {
    // Section visibility helper. When `showSections` is omitted, every section
    // is visible (preserves original modal behaviour). When provided, only the
    // listed sections render — used by WorkspaceSettingsPage to slice this
    // seven-section surface across its four top-level tabs.
    const showSection = (s) => showSections === undefined || showSections.includes(s);
    const ctx = useDataContext();
    const [handle, setHandle] = useState("");
    const [displayName, setDisplayName] = useState("");
    const [emojis, setEmojis] = useState(DEFAULT_EMOJIS);
    const [emojiInput, setEmojiInput] = useState("");
    const [loading, setLoading] = useState(true);
    const [iconUrl, setIconUrl] = useState(null);
    const [resolvedIconUrl, setResolvedIconUrl] = useState(null);
    const [iconRenderAs, setIconRenderAs] = useState("img");
    const [workspaceName, setWorkspaceName] = useState("");
    const [pendingFile, setPendingFile] = useState(null);
    const [iconError, setIconError] = useState(null);
    const fileInputRef = useRef(null);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [mcpConfig, setMcpConfig] = useState({ mcpServers: {} });
    const [mcpLoading, setMcpLoading] = useState(true);
    const [mcpEditMode, setMcpEditMode] = useState(false);
    const [mcpEditText, setMcpEditText] = useState("");
    const [mcpError, setMcpError] = useState(null);
    const [mcpSaved, setMcpSaved] = useState(false);
    const mcpTextareaRef = useRef(null);
    // KTK-242 — workspace Brave Search API key (Agent V2 web_search built-in).
    // Hub returns `{configured, masked}` so the UI never sees the raw key;
    // PUT writes the new value and Hub fans `agent_set_brave_search_api_key`
    // to every running v2 agent.
    const [braveKeyConfigured, setBraveKeyConfigured] = useState(false);
    const [braveKeyMasked, setBraveKeyMasked] = useState(null);
    const [braveKeyEditing, setBraveKeyEditing] = useState(false);
    const [braveKeyDraft, setBraveKeyDraft] = useState("");
    const [braveKeySaving, setBraveKeySaving] = useState(false);
    const [braveKeyError, setBraveKeyError] = useState(null);
    const [braveKeySaved, setBraveKeySaved] = useState(false);
    // KTK-246 — workspace OpenRouter API key (BYOK at workspace tier).
    // Mirrors the Brave key shape; Hub fans `agent_set_openrouter_api_key`.
    const [orKeyConfigured, setOrKeyConfigured] = useState(false);
    const [orKeyMasked, setOrKeyMasked] = useState(null);
    const [orKeyEditing, setOrKeyEditing] = useState(false);
    const [orKeyDraft, setOrKeyDraft] = useState("");
    const [orKeySaving, setOrKeySaving] = useState(false);
    const [orKeyError, setOrKeyError] = useState(null);
    const [orKeySaved, setOrKeySaved] = useState(false);
    // KTK-275 — workspace-default OpenRouter model. Pickable from the live
    // catalog via OpenRouterModelPicker. Per-agent `model` fields fall
    // back to this when an agent has none.
    const [orDefaultModel, setOrDefaultModel] = useState("");
    const [orDefaultModelSaving, setOrDefaultModelSaving] = useState(false);
    // KTK-246 follow-up — workspace-customizable Agent V2 default
    // system prompt template. Edited as raw text here; CreateAgentV2Dialog
    // substitutes <AGENT_NAME> + <MODEL> placeholders at create time.
    const [v2PromptTemplate, setV2PromptTemplate] = useState(CANONICAL_AGENT_V2_SYSTEM_PROMPT_TEMPLATE);
    const [v2PromptDraft, setV2PromptDraft] = useState(CANONICAL_AGENT_V2_SYSTEM_PROMPT_TEMPLATE);
    const [v2PromptSaving, setV2PromptSaving] = useState(false);
    const [v2PromptError, setV2PromptError] = useState(null);
    const [v2PromptSaved, setV2PromptSaved] = useState(false);
    // KTK-275 — Profile tab uses debounced auto-save. `identityDirty` flips
    // true the moment the user types in any identity field; the debounced
    // effect below fires `handleIdentitySave` after 800ms of quiet, then
    // clears the dirty flag and stamps `identityLastSaved`. The relative-
    // time hint ticks every 30s so "2 min ago" rolls forward without a
    // re-render storm. Initial data load doesn't trigger autosave because
    // the field setters in the load effect don't flip `identityDirty`.
    const [identityDirty, setIdentityDirty] = useState(false);
    const [identityLastSaved, setIdentityLastSaved] = useState(null);
    const [, setHintTick] = useState(0);
    useEffect(() => {
        let cancelled = false;
        Promise.all([
            ctx.hub.getWorkspaceSetting(workspaceId, "userHandle"),
            ctx.hub.getWorkspaceSetting(workspaceId, "displayName"),
            ctx.hub.getWorkspaceSetting(workspaceId, "reactionEmojis"),
            ctx.hub.listWorkspaces(),
        ])
            .then(([h, d, e, workspaces]) => {
            if (cancelled)
                return;
            if (typeof h === "string")
                setHandle(h);
            if (typeof d === "string")
                setDisplayName(d);
            if (Array.isArray(e) && e.every((v) => typeof v === "string")) {
                setEmojis(e);
            }
            const w = workspaces.find((ws) => ws.id === workspaceId);
            if (w) {
                setWorkspaceName(w.name);
                setIconUrl(w.icon_url ?? null);
            }
        })
            .catch(() => {
            /* initial load failure is fine; user can still edit */
        })
            .finally(() => {
            if (!cancelled)
                setLoading(false);
        });
        return () => {
            cancelled = true;
        };
    }, [ctx.hub, workspaceId]);
    useEffect(() => {
        let cancelled = false;
        setIconRenderAs("img");
        if (!iconUrl) {
            setResolvedIconUrl(null);
            return;
        }
        ctx.hub
            .authedImageUrl(iconUrl)
            .then((u) => {
            if (!cancelled)
                setResolvedIconUrl(u);
        })
            .catch(() => {
            if (!cancelled)
                setResolvedIconUrl(null);
        });
        return () => {
            cancelled = true;
        };
    }, [ctx.hub, iconUrl]);
    const handleIconUpload = async (args) => {
        setIconError(null);
        try {
            const res = await ctx.hub.setWorkspaceIcon(workspaceId, args.base64, args.mimeType);
            setIconUrl(res.icon_url);
            setPendingFile(null);
            onIconChange?.();
        }
        catch (e) {
            setIconError(humanizeIconError(e));
            throw e;
        }
    };
    const handleIconDelete = async () => {
        setIconError(null);
        try {
            await ctx.hub.deleteWorkspaceIcon(workspaceId);
            setIconUrl(null);
            onIconChange?.();
        }
        catch (e) {
            setIconError(humanizeIconError(e));
        }
    };
    useEffect(() => {
        let cancelled = false;
        ctx.hub
            .getMcpJsonConfig("workspace", workspaceId)
            .then((cfg) => {
            if (cancelled)
                return;
            setMcpConfig(cfg);
            setMcpEditText(JSON.stringify(stripDisabled(cfg), null, 2));
        })
            .catch((e) => {
            if (!cancelled)
                setMcpError(e instanceof Error ? e.message : String(e));
        })
            .finally(() => {
            if (!cancelled)
                setMcpLoading(false);
        });
        return () => {
            cancelled = true;
        };
    }, [ctx.hub, workspaceId]);
    // KTK-242 — initial load of the workspace's Brave Search API key
    // status. Hub returns the masked preview only; the raw key is never
    // sent to the browser.
    useEffect(() => {
        let cancelled = false;
        ctx.hub
            .getBraveSearchApiKey(workspaceId)
            .then((res) => {
            if (cancelled)
                return;
            setBraveKeyConfigured(res.configured);
            setBraveKeyMasked(res.masked);
        })
            .catch((e) => {
            if (!cancelled)
                setBraveKeyError(e instanceof Error ? e.message : String(e));
        });
        return () => {
            cancelled = true;
        };
    }, [ctx.hub, workspaceId]);
    // KTK-246 — initial load of the workspace's OpenRouter API key
    // status. Same masked-only shape as Brave.
    useEffect(() => {
        let cancelled = false;
        ctx.hub
            .getOpenRouterApiKey(workspaceId)
            .then((res) => {
            if (cancelled)
                return;
            setOrKeyConfigured(res.configured);
            setOrKeyMasked(res.masked);
        })
            .catch((e) => {
            if (!cancelled)
                setOrKeyError(e instanceof Error ? e.message : String(e));
        });
        return () => {
            cancelled = true;
        };
    }, [ctx.hub, workspaceId]);
    // KTK-275 — initial load of the workspace-default OpenRouter model.
    // Empty string when unset (the picker just shows its placeholder).
    useEffect(() => {
        let cancelled = false;
        ctx.hub
            .getWorkspaceSetting(workspaceId, OPENROUTER_DEFAULT_MODEL_KEY)
            .then((v) => {
            if (cancelled)
                return;
            if (typeof v === "string")
                setOrDefaultModel(v);
        })
            .catch(() => {
            /* no setting yet — empty default is fine */
        });
        return () => {
            cancelled = true;
        };
    }, [ctx.hub, workspaceId]);
    // Persist the workspace-default model on pick. Picker fires onChange
    // synchronously; we save in a fire-and-forget pattern so the UI feels
    // instant. Errors flip the local-only `orKeyError` channel since this
    // sits inside the OpenRouter card.
    async function handleDefaultModelChange(slug) {
        setOrDefaultModel(slug);
        if (!slug)
            return;
        setOrDefaultModelSaving(true);
        try {
            await ctx.hub.setWorkspaceSetting(workspaceId, OPENROUTER_DEFAULT_MODEL_KEY, slug);
        }
        catch (e) {
            setOrKeyError(e instanceof Error ? e.message : String(e));
        }
        finally {
            setOrDefaultModelSaving(false);
        }
    }
    // KTK-246 follow-up — load the workspace's Agent V2 default system
    // prompt template. Falls back to the canonical hardcoded template
    // when the workspace hasn't customized.
    useEffect(() => {
        let cancelled = false;
        ctx.hub
            .getWorkspaceSetting(workspaceId, V2_DEFAULT_SYSTEM_PROMPT_SETTING_KEY)
            .then((v) => {
            if (cancelled)
                return;
            if (typeof v === "string" && v.length > 0) {
                setV2PromptTemplate(v);
                setV2PromptDraft(v);
            }
        })
            .catch(() => {
            /* no setting yet — fallback canonical already in state */
        });
        return () => {
            cancelled = true;
        };
    }, [ctx.hub, workspaceId]);
    const handleBraveKeySave = async () => {
        setBraveKeySaving(true);
        setBraveKeyError(null);
        setBraveKeySaved(false);
        try {
            await ctx.hub.setBraveSearchApiKey(workspaceId, braveKeyDraft);
            // Re-fetch the masked preview so the UI reflects the new state
            // without needing to special-case the local draft.
            const res = await ctx.hub.getBraveSearchApiKey(workspaceId);
            setBraveKeyConfigured(res.configured);
            setBraveKeyMasked(res.masked);
            setBraveKeyEditing(false);
            setBraveKeyDraft("");
            setBraveKeySaved(true);
        }
        catch (e) {
            setBraveKeyError(e instanceof Error ? e.message : String(e));
        }
        finally {
            setBraveKeySaving(false);
        }
    };
    const handleBraveKeyClear = async () => {
        if (!confirm("Clear the workspace Brave Search API key? web_search will stop working until a new key is set.")) {
            return;
        }
        setBraveKeySaving(true);
        setBraveKeyError(null);
        try {
            await ctx.hub.setBraveSearchApiKey(workspaceId, "");
            setBraveKeyConfigured(false);
            setBraveKeyMasked(null);
            setBraveKeyEditing(false);
            setBraveKeyDraft("");
            setBraveKeySaved(true);
        }
        catch (e) {
            setBraveKeyError(e instanceof Error ? e.message : String(e));
        }
        finally {
            setBraveKeySaving(false);
        }
    };
    const handleOrKeySave = async () => {
        setOrKeySaving(true);
        setOrKeyError(null);
        setOrKeySaved(false);
        try {
            await ctx.hub.setOpenRouterApiKey(workspaceId, orKeyDraft);
            const res = await ctx.hub.getOpenRouterApiKey(workspaceId);
            setOrKeyConfigured(res.configured);
            setOrKeyMasked(res.masked);
            setOrKeyEditing(false);
            setOrKeyDraft("");
            setOrKeySaved(true);
        }
        catch (e) {
            setOrKeyError(e instanceof Error ? e.message : String(e));
        }
        finally {
            setOrKeySaving(false);
        }
    };
    const handleV2PromptSave = async () => {
        setV2PromptSaving(true);
        setV2PromptError(null);
        setV2PromptSaved(false);
        try {
            await ctx.hub.setWorkspaceSetting(workspaceId, V2_DEFAULT_SYSTEM_PROMPT_SETTING_KEY, v2PromptDraft);
            setV2PromptTemplate(v2PromptDraft);
            setV2PromptSaved(true);
        }
        catch (e) {
            setV2PromptError(e instanceof Error ? e.message : String(e));
        }
        finally {
            setV2PromptSaving(false);
        }
    };
    const handleV2PromptReset = () => {
        setV2PromptDraft(CANONICAL_AGENT_V2_SYSTEM_PROMPT_TEMPLATE);
        setV2PromptSaved(false);
        setV2PromptError(null);
    };
    const handleOrKeyClear = async () => {
        if (!confirm("Clear the workspace OpenRouter API key? Agent V2 turns will fail until a new key is set.")) {
            return;
        }
        setOrKeySaving(true);
        setOrKeyError(null);
        try {
            await ctx.hub.setOpenRouterApiKey(workspaceId, "");
            setOrKeyConfigured(false);
            setOrKeyMasked(null);
            setOrKeyEditing(false);
            setOrKeyDraft("");
            setOrKeySaved(true);
        }
        catch (e) {
            setOrKeyError(e instanceof Error ? e.message : String(e));
        }
        finally {
            setOrKeySaving(false);
        }
    };
    const handleMcpEdit = () => {
        setMcpEditText(JSON.stringify(stripDisabled(mcpConfig), null, 2));
        setMcpEditMode(true);
        setMcpError(null);
        setMcpSaved(false);
        setTimeout(() => mcpTextareaRef.current?.focus(), 50);
    };
    const handleMcpSave = async () => {
        try {
            const parsed = JSON.parse(mcpEditText);
            if (!parsed.mcpServers || typeof parsed.mcpServers !== "object") {
                setMcpError('Config must have a "mcpServers" object');
                return;
            }
            const disabled = mcpConfig._disabled ?? [];
            const toSave = { ...parsed, _disabled: disabled };
            await ctx.hub.setMcpJsonConfig("workspace", workspaceId, toSave);
            setMcpConfig(toSave);
            setMcpEditMode(false);
            setMcpError(null);
            setMcpSaved(true);
        }
        catch (e) {
            setMcpError(`Invalid JSON: ${e instanceof Error ? e.message : String(e)}`);
        }
    };
    const handleMcpCancel = () => {
        setMcpEditMode(false);
        setMcpEditText(JSON.stringify(stripDisabled(mcpConfig), null, 2));
        setMcpError(null);
    };
    const handleMcpToggle = async (serverName) => {
        const disabled = (mcpConfig._disabled ?? []).slice();
        const isDisabled = disabled.includes(serverName);
        const nextDisabled = isDisabled
            ? disabled.filter((n) => n !== serverName)
            : [...disabled, serverName];
        const nextConfig = { ...mcpConfig, _disabled: nextDisabled };
        await ctx.hub.setMcpJsonConfig("workspace", workspaceId, nextConfig);
        setMcpConfig(nextConfig);
    };
    const mcpServers = mcpConfig.mcpServers ?? {};
    const mcpDisabled = mcpConfig._disabled ?? [];
    const mcpEntries = Object.entries(mcpServers);
    // Deep-link scroll: when opened with `initialSection`, scroll that
    // `<section id="ws-section-…">` into view. Identity + Workspace icon
    // are gated on `loading=false`, so wait until data has landed before
    // scrolling — otherwise the target node isn't in the tree yet.
    useEffect(() => {
        if (!initialSection || loading)
            return;
        const el = document.getElementById(`ws-section-${initialSection}`);
        if (el)
            el.scrollIntoView({ behavior: "auto", block: "start" });
    }, [initialSection, loading]);
    const handleIdentitySave = async () => {
        setSaving(true);
        setError(null);
        try {
            const normalizedEmojis = emojiInput.trim()
                ? Array.from(emojiInput.trim()).filter((c) => c.trim().length > 0)
                : emojis;
            await Promise.all([
                ctx.hub.setWorkspaceSetting(workspaceId, "userHandle", handle.trim()),
                ctx.hub.setWorkspaceSetting(workspaceId, "displayName", displayName.trim()),
                ctx.hub.setWorkspaceSetting(workspaceId, "reactionEmojis", normalizedEmojis),
            ]);
            onHandleChange?.(handle.trim());
            // Promote the parsed emoji string into the canonical emoji array
            // so the next render reflects what was actually persisted, then
            // clear the raw input so the field shows the array form again.
            if (emojiInput.trim()) {
                setEmojis(normalizedEmojis);
                setEmojiInput("");
            }
            setIdentityLastSaved(new Date());
            setIdentityDirty(false);
        }
        catch (e) {
            setError(e instanceof Error ? e.message : String(e));
        }
        finally {
            setSaving(false);
        }
    };
    // KTK-275 auto-save: 800ms after the last keystroke in any identity
    // field, fire `handleIdentitySave`. The `identityDirty` gate keeps the
    // initial-load setHandle/setDisplayName/setEmojis from triggering a
    // self-save round-trip before the user has typed anything.
    useEffect(() => {
        if (!identityDirty || loading || saving)
            return;
        const t = window.setTimeout(() => {
            void handleIdentitySave();
        }, 800);
        return () => window.clearTimeout(t);
        // handleIdentitySave is stable in the closure for the duration of one
        // dirty pass — re-binding on field changes is the whole point.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [handle, displayName, emojiInput, identityDirty, loading, saving]);
    // Tick the relative-time hint every 30s so "2 min ago" rolls forward
    // without keeping a per-keystroke timer running.
    useEffect(() => {
        if (!identityLastSaved)
            return;
        const id = window.setInterval(() => setHintTick((n) => n + 1), 30_000);
        return () => window.clearInterval(id);
    }, [identityLastSaved]);
    // Body content (the seven sections, gated by `showSection`). Used by both
    // the modal and inline render paths — only the surrounding chrome differs.
    const sectionsBody = (_jsxs("div", { style: inline
            ? { display: "flex", flexDirection: "column", gap: 20 }
            : {
                padding: 20,
                overflowY: "auto",
                display: "flex",
                flexDirection: "column",
                gap: 20,
            }, children: [showSection("identity") && (loading ? (_jsx("div", { className: "ws-mono", children: "Loading identity\u2026" })) : (_jsxs(_Fragment, { children: [_jsxs("header", { className: "ws-sect__head", children: [_jsx("h2", { className: "ws-sect__h", children: "Identity" }), _jsx("p", { className: "ws-sect__sub", children: "What agents and channels call you. Per-workspace; switch workspaces to use a different handle." })] }), _jsxs("div", { className: "ws-card", children: [_jsxs("div", { className: "ws-fieldrow", children: [_jsxs("label", { className: "ws-field", children: [_jsx("span", { className: "ws-field__l", children: "User handle" }), _jsx("input", { type: "text", className: "ws-field__i mono", value: handle, onChange: (e) => {
                                                    setHandle(e.target.value);
                                                    setIdentityDirty(true);
                                                }, placeholder: "e.g. akari" }), _jsxs("span", { className: "ws-field__h", children: ["Lowercase, a\u2013z 0\u20139 hyphens. Used for", " ", _jsxs("code", { className: "ws-code", children: ["@", handle.trim() || "handle"] }), " mentions."] })] }), _jsxs("label", { className: "ws-field", children: [_jsx("span", { className: "ws-field__l", children: "Display name" }), _jsx("input", { type: "text", className: "ws-field__i", value: displayName, onChange: (e) => {
                                                    setDisplayName(e.target.value);
                                                    setIdentityDirty(true);
                                                }, placeholder: "e.g. Akari Lara" }), _jsx("span", { className: "ws-field__h", children: "Falls back to your Clerk display name when blank." })] })] }), _jsxs("label", { className: "ws-field", children: [_jsx("span", { className: "ws-field__l", children: "Reaction emojis" }), _jsx("input", { type: "text", className: "ws-field__i mono", style: { fontSize: 18, letterSpacing: 4 }, value: emojiInput || emojis.join(""), onChange: (e) => {
                                            setEmojiInput(e.target.value);
                                            setIdentityDirty(true);
                                        }, placeholder: DEFAULT_EMOJIS.join("") }), _jsxs("span", { className: "ws-field__h", children: ["Quick-react row on every message. Defaults to", " ", _jsx("code", { className: "ws-code", children: DEFAULT_EMOJIS.join("") }), "; clear to reset."] })] }), error && (_jsx("div", { style: {
                                    color: "var(--error)",
                                    fontSize: 12,
                                    padding: "8px 10px",
                                    border: "1px solid rgba(250,82,82,0.3)",
                                    borderRadius: 6,
                                }, children: error })), _jsxs("div", { className: "ws-actions", children: [_jsx("span", { className: "ws-actions__hint", children: saving
                                            ? "Saving…"
                                            : identityLastSaved
                                                ? `Saved automatically · last write ${formatRelativeTime(identityLastSaved)}`
                                                : identityDirty
                                                    ? "Auto-saving in a moment…"
                                                    : "Auto-saves on change" }), _jsx("button", { type: "button", className: "ws-btn ws-btn--primary", onClick: () => void handleIdentitySave(), disabled: saving || !identityDirty, children: saving ? "Saving…" : "Save" })] })] })] }))), showSection("icon") && !loading && (_jsxs(_Fragment, { children: [_jsxs("header", { className: "ws-sect__head", children: [_jsx("h2", { className: "ws-sect__h", children: "Workspace icon" }), _jsx("p", { className: "ws-sect__sub", children: "PNG \u00B7 JPEG \u00B7 WebP \u00B7 GIF \u00B7 SVG (\u2264 2 MB) \u00B7 MP4 \u00B7 WebM (\u2264 4 MB). Falls back to a tinted glyph when unset." })] }), _jsxs("div", { className: "ws-card", children: [_jsxs("div", { className: "ws-icon-row", children: [_jsx("div", { className: "ws-icon-tile", children: resolvedIconUrl ? (iconRenderAs === "img" ? (_jsx("img", { src: resolvedIconUrl, alt: "", onError: () => setIconRenderAs("video") })) : (_jsx("video", { src: resolvedIconUrl, autoPlay: true, loop: true, muted: true, playsInline: true }))) : (_jsx("span", { style: { userSelect: "none" }, children: computeInitials(workspaceName || "??") })) }), _jsxs("div", { className: "ws-icon-actions", children: [_jsx("input", { ref: fileInputRef, type: "file", accept: ICON_ALLOWED_ACCEPT, style: { display: "none" }, onChange: (e) => {
                                                    const f = e.target.files?.[0] ?? null;
                                                    setPendingFile(f);
                                                    // Reset so picking the same file twice still triggers.
                                                    e.target.value = "";
                                                } }), _jsxs("div", { className: "ws-row", children: [_jsx("button", { type: "button", className: "ws-btn", onClick: () => fileInputRef.current?.click(), children: iconUrl ? "Replace image…" : "Upload image…" }), iconUrl && (_jsx("button", { type: "button", className: "ws-btn", onClick: handleIconDelete, children: "Remove" }))] }), _jsx("span", { className: "ws-field__h", children: "Recommended 256\u00D7256 px. Renders 28px in the workspace rail and 64px on this page." })] })] }), iconError && (_jsx("div", { style: {
                                    color: "var(--error)",
                                    fontSize: 12,
                                    padding: "8px 10px",
                                    border: "1px solid rgba(250,82,82,0.3)",
                                    borderRadius: 6,
                                }, children: iconError }))] })] })), showSection("mcp") && (_jsxs(_Fragment, { children: [_jsxs("header", { className: "ws-sect__head", children: [_jsx("h2", { className: "ws-sect__h", children: "MCP servers" }), _jsxs("p", { className: "ws-sect__sub", children: ["Workspace-default servers, merged into every agent's launcher. Same shape as ", _jsx("code", { className: "ws-code", children: ".mcp.json" }), ". Env vars like ", _jsx("code", { className: "ws-code", children: "${VAR_NAME}" }), " ", "interpolate from host env. Restart agents to apply."] })] }), _jsxs("div", { className: "ws-card", children: [mcpError && (_jsx("div", { style: {
                                    color: "var(--error)",
                                    fontSize: 12,
                                    padding: "8px 10px",
                                    border: "1px solid rgba(250,82,82,0.3)",
                                    borderRadius: 6,
                                }, children: mcpError })), _jsxs("div", { className: "ws-toolbar ws-toolbar--inline", children: [_jsxs("span", { className: "ws-toolbar__l", children: [_jsxs("span", { className: "ws-mono", children: [mcpEntries.length, " configured"] }), " ", "\u00B7", " ", _jsxs("span", { className: "ws-mono", children: [mcpEntries.filter(([n]) => !mcpDisabled.includes(n)).length, " ", "enabled"] }), mcpSaved && !mcpEditMode && (_jsxs(_Fragment, { children: [" ", "\u00B7", " ", _jsx("span", { className: "ws-pill ws-pill--ok", children: "saved \u00B7 restart agents" })] }))] }), !mcpLoading &&
                                        (mcpEditMode ? (_jsxs("div", { className: "ws-row", children: [_jsx("button", { type: "button", className: "ws-btn", onClick: handleMcpCancel, children: "Cancel" }), _jsx("button", { type: "button", className: "ws-btn ws-btn--primary", onClick: () => void handleMcpSave(), children: "Save JSON" })] })) : (_jsx("button", { type: "button", className: "ws-btn", onClick: handleMcpEdit, children: "Edit JSON\u2026" })))] }), mcpLoading ? (_jsx("span", { className: "ws-mono", style: { padding: "8px 0" }, children: "Loading MCP config\u2026" })) : mcpEditMode ? (_jsx("textarea", { ref: mcpTextareaRef, value: mcpEditText, onChange: (e) => setMcpEditText(e.target.value), spellCheck: false, className: "ws-ta ws-ta--prompt", style: { minHeight: 280 }, onKeyDown: (e) => {
                                    if (e.key === "Tab") {
                                        e.preventDefault();
                                        const ta = e.currentTarget;
                                        const start = ta.selectionStart;
                                        const end = ta.selectionEnd;
                                        setMcpEditText(mcpEditText.substring(0, start) +
                                            "  " +
                                            mcpEditText.substring(end));
                                        setTimeout(() => {
                                            ta.selectionStart = ta.selectionEnd = start + 2;
                                        }, 0);
                                    }
                                    if ((e.ctrlKey || e.metaKey) && e.key === "s") {
                                        e.preventDefault();
                                        void handleMcpSave();
                                    }
                                } })) : mcpEntries.length === 0 ? (_jsx("div", { style: {
                                    padding: "20px 14px",
                                    textAlign: "center",
                                    color: "var(--ink-3)",
                                    fontFamily: "var(--serif)",
                                    fontStyle: "italic",
                                    fontSize: 12.5,
                                }, children: "No MCP servers configured. Click \"Edit JSON\u2026\" to add servers." })) : (_jsx("div", { className: "ws-mcp", children: mcpEntries.map(([name, srv]) => {
                                    const config = srv;
                                    const enabled = !mcpDisabled.includes(name);
                                    const isHttp = config.type === "http";
                                    const command = config.command;
                                    const args = config.args ?? [];
                                    const url = config.url;
                                    const subtitle = isHttp
                                        ? url ?? "(http)"
                                        : [command, ...args].filter(Boolean).join(" ");
                                    return (_jsxs("div", { className: `ws-mcp__row${enabled ? "" : " is-disabled"}`, children: [_jsxs("label", { className: "ws-toggle", children: [_jsx("input", { type: "checkbox", checked: enabled, onChange: () => void handleMcpToggle(name) }), _jsx("span", { className: "ws-toggle__track", children: _jsx("span", { className: "ws-toggle__thumb" }) })] }), _jsxs("div", { className: "ws-mcp__main", children: [_jsx("div", { className: "ws-mcp__name", children: name }), _jsx("div", { className: "ws-mcp__cmd", children: subtitle })] }), _jsx("span", { className: `ws-pill ${enabled ? "ws-pill--ok" : "ws-pill--muted"}`, children: enabled ? "enabled" : "off" })] }, name));
                                }) }))] })] })), showSection("search") && (_jsxs(_Fragment, { children: [_jsxs("header", { className: "ws-sect__head", children: [_jsx("h2", { className: "ws-sect__h", children: "Search providers" }), _jsxs("p", { className: "ws-sect__sub", children: ["Brave Search API key powers the Agent V2", " ", _jsx("code", { className: "ws-code", children: "web_search" }), " built-in. Workspace-scoped; takes effect on the next agent turn."] })] }), _jsxs("div", { className: "ws-card", children: [braveKeyError && (_jsx("div", { style: {
                                    color: "var(--error)",
                                    fontSize: 12,
                                    padding: "8px 10px",
                                    border: "1px solid rgba(250,82,82,0.3)",
                                    borderRadius: 6,
                                }, children: braveKeyError })), _jsxs("div", { className: "ws-field", children: [_jsx("span", { className: "ws-field__l", children: "Brave API key" }), braveKeyEditing ? (_jsxs("div", { className: "ws-row", children: [_jsx("input", { 
                                                // Akari 2026-04-28: stop browsers (Chrome, Apple
                                                // Keychain, 1Password, LastPass) from treating
                                                // API-key fields as login passwords and triggering
                                                // save prompts. `type="password"` +
                                                // `autoComplete="new-password"` + opt-out attrs is
                                                // what Chrome's password manager STILL doesn't
                                                // honor — it pops "Save password for
                                                // app.kritaka.io?" every time. Switch to
                                                // `type="text"` and mask via CSS so the value
                                                // renders as dots without being eligible for the
                                                // autosave heuristic.
                                                type: "text", className: "ws-field__i mono", autoFocus: true, value: braveKeyDraft, onChange: (e) => setBraveKeyDraft(e.target.value), placeholder: "Brave Search API key", name: "brave-search-api-key-anon", autoComplete: "off", "data-1p-ignore": "true", "data-lpignore": "true", "data-form-type": "other", spellCheck: false, style: {
                                                    flex: 1,
                                                    WebkitTextSecurity: "disc",
                                                }, onKeyDown: (e) => {
                                                    if (e.key === "Enter" && braveKeyDraft.length > 0) {
                                                        e.preventDefault();
                                                        void handleBraveKeySave();
                                                    }
                                                    if (e.key === "Escape") {
                                                        e.preventDefault();
                                                        setBraveKeyEditing(false);
                                                        setBraveKeyDraft("");
                                                    }
                                                } }), _jsx("button", { type: "button", className: "ws-btn", onClick: () => {
                                                    setBraveKeyEditing(false);
                                                    setBraveKeyDraft("");
                                                }, disabled: braveKeySaving, children: "Cancel" }), _jsx("button", { type: "button", className: "ws-btn ws-btn--primary", onClick: () => void handleBraveKeySave(), disabled: braveKeySaving || braveKeyDraft.length === 0, children: braveKeySaving ? "Saving…" : "Save" })] })) : (_jsxs("div", { className: "ws-row", children: [_jsx("span", { className: "ws-field__i mono", style: {
                                                    flex: 1,
                                                    color: braveKeyConfigured ? "var(--ink)" : "var(--ink-3)",
                                                    display: "inline-flex",
                                                    alignItems: "center",
                                                }, children: braveKeyConfigured
                                                    ? braveKeyMasked ?? "•••• configured"
                                                    : "Not configured" }), _jsx("button", { type: "button", className: "ws-btn", onClick: () => setBraveKeyEditing(true), children: braveKeyConfigured ? "Replace" : "Set key" }), braveKeyConfigured && (_jsx("button", { type: "button", className: "ws-btn ws-btn--danger", onClick: () => void handleBraveKeyClear(), disabled: braveKeySaving, children: "Clear" }))] })), _jsx("span", { className: "ws-field__h", children: braveKeyConfigured ? (_jsxs(_Fragment, { children: [_jsx("span", { className: "ws-pill ws-pill--ok", children: "configured" }), " \u00B7 free tier: 2,000 queries/month at", " ", _jsx("code", { className: "ws-code", children: "api.search.brave.com" }), "."] })) : (_jsxs(_Fragment, { children: ["Get a key at", " ", _jsx("code", { className: "ws-code", children: "api.search.brave.com" }), ". Free tier: 2,000 queries/month."] })) })] })] })] })), showSection("openrouter") && (_jsxs(_Fragment, { children: [_jsxs("header", { className: "ws-sect__head", children: [_jsx("h2", { className: "ws-sect__h", children: "OpenRouter" }), _jsx("p", { className: "ws-sect__sub", children: "Default model gateway for Agent V2. Per-agent model picks fall back to this when an agent has none." })] }), _jsxs("div", { className: "ws-card", children: [orKeyError && (_jsx("div", { style: {
                                    color: "var(--error)",
                                    fontSize: 12,
                                    padding: "8px 10px",
                                    border: "1px solid rgba(250,82,82,0.3)",
                                    borderRadius: 6,
                                }, children: orKeyError })), _jsxs("div", { className: "ws-field", children: [_jsx("span", { className: "ws-field__l", children: "API key" }), orKeyEditing ? (_jsxs("div", { className: "ws-row", children: [_jsx("input", { type: "text", className: "ws-field__i mono", autoFocus: true, value: orKeyDraft, onChange: (e) => setOrKeyDraft(e.target.value), placeholder: "sk-or-v1-\u2026", name: "openrouter-api-key-anon", autoComplete: "off", "data-1p-ignore": "true", "data-lpignore": "true", "data-form-type": "other", spellCheck: false, style: {
                                                    flex: 1,
                                                    WebkitTextSecurity: "disc",
                                                }, onKeyDown: (e) => {
                                                    if (e.key === "Enter" && orKeyDraft.length > 0) {
                                                        e.preventDefault();
                                                        void handleOrKeySave();
                                                    }
                                                    if (e.key === "Escape") {
                                                        e.preventDefault();
                                                        setOrKeyEditing(false);
                                                        setOrKeyDraft("");
                                                    }
                                                } }), _jsx("button", { type: "button", className: "ws-btn", onClick: () => {
                                                    setOrKeyEditing(false);
                                                    setOrKeyDraft("");
                                                }, disabled: orKeySaving, children: "Cancel" }), _jsx("button", { type: "button", className: "ws-btn ws-btn--primary", onClick: () => void handleOrKeySave(), disabled: orKeySaving || orKeyDraft.length === 0, children: orKeySaving ? "Saving…" : "Save" })] })) : (_jsxs("div", { className: "ws-row", children: [_jsx("span", { className: "ws-field__i mono", style: {
                                                    flex: 1,
                                                    color: orKeyConfigured ? "var(--ink)" : "var(--ink-3)",
                                                    display: "inline-flex",
                                                    alignItems: "center",
                                                }, children: orKeyConfigured
                                                    ? orKeyMasked ?? "•••• configured"
                                                    : "Not configured" }), _jsx("button", { type: "button", className: "ws-btn", onClick: () => setOrKeyEditing(true), children: orKeyConfigured ? "Replace" : "Set key" }), orKeyConfigured && (_jsx("button", { type: "button", className: "ws-btn ws-btn--danger", onClick: () => void handleOrKeyClear(), disabled: orKeySaving, children: "Clear" }))] })), _jsx("span", { className: "ws-field__h", children: orKeyConfigured ? (_jsxs(_Fragment, { children: [_jsx("span", { className: "ws-pill ws-pill--ok", children: "configured" }), " \u00B7 every Agent V2 turn + journalist cycle in this workspace uses this key."] })) : (_jsxs(_Fragment, { children: ["Get a key at", " ", _jsx("code", { className: "ws-code", children: "openrouter.ai/keys" }), ". Required for Agent V2 turns and journalist runs."] })) })] }), _jsxs("div", { className: "ws-field", children: [_jsx("span", { className: "ws-field__l", children: "Default model" }), _jsx(OpenRouterModelPicker, { value: orDefaultModel, onChange: (slug) => void handleDefaultModelChange(slug) }), _jsx("span", { className: "ws-field__h", children: orDefaultModel ? (_jsxs(_Fragment, { children: ["Default is", " ", _jsx("code", { className: "ws-code", children: orDefaultModel }), ". Type to fuzzy-search OpenRouter's full catalog. Per-agent picks override this.", orDefaultModelSaving && _jsx(_Fragment, { children: " \u00B7 saving\u2026" })] })) : (_jsx(_Fragment, { children: "No workspace default. Each agent must set its own model. Pick one to provide a fallback." })) })] })] })] })), showSection("agent-prompt") && (_jsxs(_Fragment, { children: [_jsxs("header", { className: "ws-sect__head", children: [_jsx("h2", { className: "ws-sect__h", children: "Agent V2 system prompt" }), _jsxs("p", { className: "ws-sect__sub", children: ["Base prompt every new Agent V2 in this workspace is created with. ", _jsx("code", { className: "ws-code", children: "<AGENT_NAME>" }), " and", " ", _jsx("code", { className: "ws-code", children: "<MODEL>" }), " are substituted at create time. Per-agent prompts can override."] })] }), _jsxs("div", { className: "ws-card", children: [v2PromptError && (_jsx("div", { style: {
                                    color: "var(--error)",
                                    fontSize: 12,
                                    padding: "8px 10px",
                                    border: "1px solid rgba(250,82,82,0.3)",
                                    borderRadius: 6,
                                }, children: v2PromptError })), _jsx("textarea", { className: "ws-ta ws-ta--prompt", value: v2PromptDraft, onChange: (e) => {
                                    setV2PromptDraft(e.target.value);
                                    setV2PromptSaved(false);
                                }, spellCheck: false, style: { minHeight: 320, whiteSpace: "pre" } }), _jsxs("div", { className: "ws-actions", children: [_jsx("span", { className: "ws-actions__hint", children: v2PromptSaved ? (_jsxs(_Fragment, { children: [_jsx("span", { className: "ws-pill ws-pill--ok", children: "saved" }), " \u00B7 new agents pick it up immediately."] })) : v2PromptDraft !== v2PromptTemplate ? (_jsx(_Fragment, { children: "Edited \u00B7 save to apply." })) : (_jsx(_Fragment, { children: "Defaults from the canonical template." })) }), _jsxs("div", { className: "ws-row", children: [_jsx("button", { type: "button", className: "ws-btn", onClick: handleV2PromptReset, disabled: v2PromptSaving ||
                                                    v2PromptDraft === CANONICAL_AGENT_V2_SYSTEM_PROMPT_TEMPLATE, children: "Reset to default" }), _jsx("button", { type: "button", className: "ws-btn ws-btn--primary", onClick: () => void handleV2PromptSave(), disabled: v2PromptSaving || v2PromptDraft === v2PromptTemplate, children: v2PromptSaving ? "Saving…" : "Save template" })] })] })] })] })), showSection("spend") && (_jsxs(_Fragment, { children: [_jsxs("header", { className: "ws-sect__head", children: [_jsx("h2", { className: "ws-sect__h", children: "Spend" }), _jsx("p", { className: "ws-sect__sub", children: "Per-agent + workspace ceilings. Crossing a soft cap surfaces a decision card; hard cap halts the agent." })] }), _jsx("div", { className: "ws-card", children: _jsx(SpendDashboard, { workspaceId: workspaceId }) })] }))] }));
    // Save Identity is the only mutation here that doesn't have its own inline
    // save button — every other section persists its own state. In inline mode
    // we surface the Save button at the bottom of the body so the Profile tab's
    // Identity section is still committable; in modal mode it lives in the
    // dialog footer beside Close.
    const saveIdentityBtn = showSection("identity") ? (_jsx("button", { type: "button", onClick: () => void handleIdentitySave(), disabled: saving || loading, style: {
            ...btnStyle,
            background: saving || loading ? "var(--bg-hover)" : "var(--accent)",
            color: "white",
            cursor: saving || loading ? "not-allowed" : "pointer",
        }, children: saving ? "Saving…" : "Save identity" })) : null;
    const iconCropper = pendingFile ? (_jsx(IconCropper, { file: pendingFile, onCancel: () => setPendingFile(null), onConfirm: handleIconUpload })) : null;
    if (inline) {
        return (_jsxs("div", { style: { display: "flex", flexDirection: "column", gap: 16 }, children: [sectionsBody, saveIdentityBtn && (_jsx("div", { style: { display: "flex", justifyContent: "flex-end" }, children: saveIdentityBtn })), iconCropper] }));
    }
    return (_jsxs("div", { style: {
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.5)",
            display: "grid",
            placeItems: "center",
            zIndex: 100,
        }, children: [_jsxs("div", { style: {
                    background: "var(--bg-secondary)",
                    border: "1px solid var(--border)",
                    borderRadius: 10,
                    width: 640,
                    maxHeight: "90vh",
                    display: "flex",
                    flexDirection: "column",
                    overflow: "hidden",
                }, children: [_jsxs("div", { style: {
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            padding: "14px 20px",
                            borderBottom: "1px solid var(--border)",
                        }, children: [_jsx("h2", { style: { margin: 0, fontSize: 16, color: "var(--text-primary)" }, children: "Workspace settings" }), _jsx("button", { type: "button", onClick: onClose, style: closeBtnStyle, "aria-label": "Close", children: "\u00D7" })] }), sectionsBody, _jsxs("div", { style: {
                            display: "flex",
                            justifyContent: "flex-end",
                            gap: 8,
                            padding: "14px 20px",
                            borderTop: "1px solid var(--border)",
                        }, children: [_jsx("button", { type: "button", onClick: onClose, style: { ...btnStyle, background: "transparent", color: "var(--text-secondary)" }, children: "Close" }), saveIdentityBtn] })] }), iconCropper] }));
}
// Surface common Hub statuses with copy that's actionable from the dialog.
function humanizeIconError(e) {
    const msg = e instanceof Error ? e.message : String(e);
    if (/\b413\b/.test(msg)) {
        return "File too large. Cap is 2 MB for images, 4 MB for video.";
    }
    if (/\b400\b/.test(msg)) {
        return "Format not allowed. Use PNG, JPEG, WebP, GIF, SVG, MP4, or WebM.";
    }
    if (/\b403\b/.test(msg))
        return "Only the workspace owner can change the icon.";
    if (/\b404\b/.test(msg))
        return "Workspace not found or you don't have access.";
    return msg || "Upload failed.";
}
function stripDisabled(cfg) {
    const { _disabled: _omit, ...rest } = cfg;
    return rest;
}
// Modal-mode chrome only: the workspace settings dialog is mounted inline
// inside WorkspaceSettingsPage in production. The modal wrapper is kept as
// a fallback for surfaces that mount the dialog directly; these are the
// last two style consts the modal still references after the KTK-275
// inline-mode refactor.
const btnStyle = {
    padding: "8px 16px",
    fontSize: 13,
    fontWeight: 500,
    border: "none",
    borderRadius: 6,
    cursor: "pointer",
    fontFamily: "inherit",
};
const closeBtnStyle = {
    background: "none",
    border: "none",
    cursor: "pointer",
    color: "var(--text-muted)",
    fontSize: 22,
    padding: "0 6px",
    lineHeight: 1,
};
//# sourceMappingURL=WorkspaceSettingsDialog.js.map